import React, { useState } from 'react'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'

import useText from '../../../../../common/hooks/useText'
import { NfcTag } from '../../../../../common/redux/api/web-api'
import { Dialog } from '../../../../../common/uikit/Dialog'
import { Input } from '../../../../../common/uikit/Input'

const MAX_LENGTH = 40
const formId = 'bulk-edit-rfid-tags-form'

export const BulkEditRfidTagsDialog: React.FC<BulkEditRfidTagsDialogProps> = ({
    visible,
    nfcTags,
    onSubmit,
    onHide,
}) => {
    const text = useText()
    const [clearComment, setClearComment] = useState(false)
    const [clearExternalId, setClearExternalId] = useState(false)
    const [rfidTags, setRfidTags] = useState<NfcTag[]>(nfcTags)
    const [comment, setComment] = useState('')
    const [externalId, setExternalId] = useState('')

    const placeholder = (value: string, clearFlag: boolean) => {
        if (clearFlag) return text('msgValueWillBeCleared')
        if (!value) return text('msgKeepWithoutChanges')
        return text('msgClearTheValue')
    }

    const toggleSelect = (rfidTag: NfcTag) => {
        if (rfidTags.some((tag) => tag.id === rfidTag.id)) {
            setRfidTags(rfidTags.filter((tag) => tag.id !== rfidTag.id))
        } else {
            setRfidTags([...rfidTags, rfidTag])
        }
    }

    const toggleSelectAll = () => {
        setRfidTags(rfidTags.length === nfcTags.length ? [] : nfcTags)
    }

    const save = async () => {
        await onSubmit(
            rfidTags.map((tag) => ({
                ...tag,
                comment: clearComment || comment.length ? comment : tag.comment,
                externalId: clearExternalId || externalId.length ? externalId : tag.externalId,
            }))
        )
    }

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" text onClick={onHide} />
            <Button label={text('btnSave')} type="submit" form={formId} disabled={!rfidTags.length} />
        </div>
    )

    return (
        <Dialog
            header={text('lblBulkEditRfidTagsDialogTitle')}
            visible={visible}
            onHide={onHide}
            footer={footer}
            data-testid="bulk-edit-rfid-tags-dialog"
        >
            <form
                id={formId}
                className="flex flex-column gap-4"
                onSubmit={(event) => {
                    event.preventDefault()
                    void save()
                }}
            >
                <section aria-labelledby="bulk-edit-rfid-tags-selection-label">
                    <div className="flex align-items-center justify-content-between gap-2 mb-2">
                        <span id="bulk-edit-rfid-tags-selection-label" className="p-label">
                            {text('msgSelectRfidTagsToUpdate')}
                        </span>
                        <Button
                            label={text(rfidTags.length === nfcTags.length ? 'btnUnselectAll' : 'btnSelectAll')}
                            type="button"
                            text
                            size="small"
                            onClick={toggleSelectAll}
                        />
                    </div>
                    <div
                        className="flex flex-wrap gap-3"
                        role="group"
                        aria-labelledby="bulk-edit-rfid-tags-selection-label"
                    >
                        {nfcTags.map((nfcTag) => {
                            const inputId = `bulk-edit-rfid-tag-${nfcTag.id}`

                            return (
                                <div className="flex align-items-center gap-2" key={nfcTag.id}>
                                    <Checkbox
                                        inputId={inputId}
                                        checked={rfidTags.some((tag) => tag.id === nfcTag.id)}
                                        onChange={() => toggleSelect(nfcTag)}
                                    />
                                    <label htmlFor={inputId} className="mb-0 cursor-pointer">
                                        {nfcTag.id}
                                    </label>
                                </div>
                            )
                        })}
                    </div>
                </section>

                <div className="flex flex-column gap-2">
                    <div className="flex flex-column">
                        <label htmlFor="bulk-edit-rfid-tags-comment" className="p-label">
                            {text('lblComment')}
                        </label>
                        <small id="bulk-edit-rfid-tags-comment-help" className="text-color-secondary">
                            {text('msgBulkEditRfidTagsClearCommentHelp')}
                        </small>
                    </div>
                    <Input
                        id="bulk-edit-rfid-tags-comment"
                        aria-describedby="bulk-edit-rfid-tags-comment-help"
                        placeholder={placeholder(comment, clearComment)}
                        disabled={clearComment}
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                    />
                    <div className="flex align-items-center gap-2">
                        <Checkbox
                            inputId="clear-bulk-edit-rfid-tags-comment"
                            checked={clearComment}
                            aria-label={text('lblClearCommentAction')}
                            onChange={(event) => {
                                const checked = event.checked ?? false
                                setClearComment(checked)
                                if (checked) setComment('')
                            }}
                        />
                        <label htmlFor="clear-bulk-edit-rfid-tags-comment" className="mb-0 cursor-pointer">
                            {text('lblClearExistingValuesAction')}
                        </label>
                    </div>
                </div>

                <div className="flex flex-column gap-2">
                    <div className="flex flex-column">
                        <label htmlFor="bulk-edit-rfid-tags-external-id" className="p-label">
                            {text('lblExternalIdField')}
                        </label>
                        <small id="bulk-edit-rfid-tags-external-id-help" className="text-color-secondary">
                            {text('msgBulkEditRfidTagsClearExternalIdHelp')}
                        </small>
                    </div>
                    <Input
                        id="bulk-edit-rfid-tags-external-id"
                        aria-describedby="bulk-edit-rfid-tags-external-id-help"
                        placeholder={placeholder(externalId, clearExternalId)}
                        disabled={clearExternalId}
                        value={externalId}
                        onChange={(event) => setExternalId(event.target.value)}
                        maxLength={MAX_LENGTH}
                    />
                    <div className="flex align-items-center gap-2">
                        <Checkbox
                            inputId="clear-bulk-edit-rfid-tags-external-id"
                            checked={clearExternalId}
                            aria-label={text('lblClearExternalIdAction')}
                            onChange={(event) => setClearExternalId(event.checked ?? false)}
                        />
                        <label htmlFor="clear-bulk-edit-rfid-tags-external-id" className="mb-0 cursor-pointer">
                            {text('lblClearExistingValuesAction')}
                        </label>
                    </div>
                </div>
            </form>
        </Dialog>
    )
}

export type BulkEditRfidTagsDialogProps = {
    visible: boolean
    nfcTags: NfcTag[]
    onSubmit: (result: NfcTag[]) => Promise<void>
    onHide: () => void
}
